import {
  Questionnaire,
  QuestionnaireTemplate,
} from '@app/core/models/questionnaire.model';
import { Question } from '@app/core/models/questionnaire.model';
import { EditingSection } from '../section-editors/editing-section';
import type { PreviewContext } from './preview.types';

/**
 * Builds a PreviewContext for runtime survey display (e.g. QR-based page).
 * No editing state; template is derived from questionnaire.
 */
export function buildRuntimePreviewContext(
  questionnaire: Questionnaire | null,
  editingSection: EditingSection,
  options?: {
    currentPage?: number;
    getCurrentPageQuestions?: () => Question[];
    getDisplayPageNumber?: () => number;
    getDisplayTotalPages?: () => number;
    canGoToNextPage?: () => boolean;
    canGoToPreviousPage?: () => boolean;
    shouldShowUserInfo?: () => boolean;
  }
): PreviewContext {
  const previewTemplate = questionnaire
    ? buildPreviewTemplateFromQuestionnaire(questionnaire)
    : null;

  return {
    questionnaire,
    editingTemplate: null,
    editingTemplateId: null,
    editingSection,
    selectedTemplateId: questionnaire?.brandingTemplateId ?? null,
    previewTemplate,
    currentTemplate: previewTemplate,
    currentPage: options?.currentPage ?? 0,
    getCurrentPageQuestions: options?.getCurrentPageQuestions,
    getDisplayPageNumber: options?.getDisplayPageNumber,
    getDisplayTotalPages: options?.getDisplayTotalPages,
    canGoToNextPage: options?.canGoToNextPage,
    canGoToPreviousPage: options?.canGoToPreviousPage,
    shouldShowUserInfo: options?.shouldShowUserInfo,
  };
}

function buildPreviewTemplateFromQuestionnaire(
  q: Questionnaire
): QuestionnaireTemplate {
  const hf = q.headerFooterSetup;
  const headerOn =
    (hf as { enableHeader?: boolean })?.enableHeader === true ||
    hf?.enableHeader === true;
  const footerOn =
    (hf as { enableFooter?: boolean })?.enableFooter === true ||
    hf?.enableFooter === true;

  const ws = q.welcomeScreenTemplate;
  const welcomeOn =
    (ws as { enableWelcomeScreen?: boolean })?.enableWelcomeScreen !== false;

  const es = q.exitScreenTemplate;
  const exitOn =
    (es as { enableExitScreen?: boolean })?.enableExitScreen !== false;

  return {
    templateId: q.brandingTemplateId || '',
    templateName: q.brandingTemplateName || 'Preview',
    ...(ws && welcomeOn && {
      welcomeScreenTemplate: JSON.parse(JSON.stringify(ws)),
    }),
    ...(es && exitOn && {
      exitScreenTemplate: JSON.parse(JSON.stringify(es)),
    }),
    mainContentTemplate: JSON.parse(
      JSON.stringify(q.mainContentTemplate || {})
    ),
    ...(q.pageTemplate && {
      pageTemplate: JSON.parse(JSON.stringify(q.pageTemplate)),
    }),
    ...(hf && (headerOn || footerOn) && {
      headerFooterSetup: JSON.parse(JSON.stringify(hf)),
    }),
  } as QuestionnaireTemplate;
}
